/**
 * withRouteTracking — automatic arrival tracking for every route.
 *
 * Wraps `createBrowserRouter` so that every navigation emits one arrival event.
 * No page opts in, so no page can opt out, and swapping the component behind a
 * route cannot silently drop its tracking.
 *
 * ─── Why this exists ────────────────────────────────────────────────────────
 *
 * Tracking used to be per-page: a developer imported `useTrackImpression` and
 * remembered to call it. `CockpitV3` was instrumented on 2026-04-11. `PlayPage`
 * replaced it on 2026-06-07 without a tracking call, and the landing page of the
 * product emitted nothing for three months — 5,766,981 cockpit impressions in
 * April, zero from July onward. Nobody noticed, because no view shows a metric
 * reaching zero.
 *
 * Voluntary coverage fails the same way every time. This makes arrival coverage
 * a property of the router rather than of whoever wrote the page.
 *
 * ─── Composing with Sentry ──────────────────────────────────────────────────
 *
 * `Sentry.wrapCreateBrowserRouter` has exactly this shape and is the precedent
 * this follows. Compose them:
 *
 *     withRouteTracking(Sentry.wrapCreateBrowserRouter(createBrowserRouter))(routes)
 *
 * A hand-applied wrapper drifts — Sentry's sits on three of five router sites
 * today. `scripts/audit-route-coverage.ts` is what keeps this one on all five.
 */

import type {createBrowserRouter as CreateBrowserRouter} from "react-router";
import {RouteEntryMethod} from "@devour/client";
import analytics from "@/utils/analytics";

type RouterFactory = typeof CreateBrowserRouter;

/**
 * The route PATTERN for the deepest match, e.g. "/drop-in/:slug".
 *
 * The pattern, not the resolved path, is what makes the data groupable: every
 * visit to any creator's page aggregates under one row instead of scattering
 * across thousands of distinct URLs. The resolved path is sent alongside it.
 */
/*
 * Internal on purpose. Section-view tracking needs the SAME value, but it reads it
 * from `currentRoute()` rather than recomputing it — a component has no access to
 * real match objects, and a second derivation would drift. Both event kinds land in
 * one table where a mismatched `route` silently breaks every join between them.
 */
function patternOf(matches: ReadonlyArray<{ route?: { path?: string } }> | undefined): string {
    if (!matches?.length) return "unmatched";
    /*
     * Join the nested segments rather than taking only the leaf: a child route
     * declares a relative path, so the leaf alone (":slug") is meaningless without
     * its parent. Empty segments are layout routes and contribute nothing.
     */
    const segments = matches
        .map((m) => m.route?.path ?? "")
        .filter((p) => p !== "" && p !== "/");
    if (!segments.length) return "/";
    return segments
        .map((p) => p.startsWith("/") ? p : `/${p}`)
        .join("")
        .replace(/\/{2,}/g, "/");
}

let currentRoutePattern = "unmatched";

/**
 * The pattern for the route on screen right now, published by the router wrapper.
 *
 * Section-view rows must carry the SAME `route` as the arrival row they belong to
 * or the join between them breaks. The router is the only place that holds real
 * match objects — React Router's public `useMatches()` returns `UIMatch`, which has
 * no route pattern on it at all, so deriving it in a component is not possible.
 */
export function currentRoute(): string {
    return currentRoutePattern;
}

export function withRouteTracking(factory: RouterFactory): RouterFactory {
    return ((routes, opts) => {
        const router = factory(routes, opts);

        /*
         * `subscribe` fires on every navigation but NOT for the first paint, so the
         * initial landing is recorded separately below. Missing it would lose the
         * single most valuable event: how someone entered the site.
         */
        let isFirst = true;

        const record = (entryMethod: RouteEntryMethod, state: { location: { pathname: string; search: string }; matches?: ReadonlyArray<{ route?: { path?: string } }> }) => {
            currentRoutePattern = patternOf(state.matches);
            try {
                void analytics.trackArrival({
                    route: currentRoutePattern,
                    path: state.location.pathname + state.location.search,
                    entryMethod,
                    referrer: entryMethod === RouteEntryMethod.direct ? document.referrer || undefined : undefined,
                });
            } catch {
                // Analytics must never break navigation.
            }
        };

        record(RouteEntryMethod.direct, router.state);

        router.subscribe((state) => {
            /*
             * The first subscribe callback can replay the state we just recorded as the
             * direct arrival; skipping it avoids double-counting every landing.
             */
            if (isFirst) {
                isFirst = false;
                return;
            }
            if (state.navigation.state !== "idle") return;
            record(RouteEntryMethod.navigation, state);
        });

        return router;
    }) as RouterFactory;
}
